import { useEffect, useMemo } from 'react';
import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAdminUsersQuery } from '../../../api/gql';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { PageHeader } from '../../../components/PageHeader';
import { useControlledRowStateModel, useControlledTableModel } from '../../../hooks/useControlledTableModel';
import { PageContent, PageLayout } from '../../../layout';
import { notify } from '../../../utils/notification';
import { AdminUsersTable } from './modules/AdminUsersTable';

export const AdminUsersPage = () => {
  const { t } = useTranslation('page-admin-users');

  const { query, debouncedQuery, skip, handlers, paginationModel } = useControlledTableModel({
    pageSize: 25,
  });

  const { data, isFetching, isError, error } = useAdminUsersQuery(
    { query: debouncedQuery, take: paginationModel.pageSize, skip: skip },
    {
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({ message: t('query.error') });
    }
  }, [error, t]);

  const { rowCount } = useControlledRowStateModel(data?.adminUsers.totalCount);
  const users = useMemo(() => data?.adminUsers.results, [data]);

  return (
    <PageLayout>
      <Grid2 container>
        <Grid2 xs={12} md={4}>
          <PageHeader title={t('title')} backRouteTo={null} />
        </Grid2>
        <Grid2 xs={12} md={8}>
          <TextField
            fullWidth
            placeholder={t('search') ?? ''}
            onChange={(e) => handlers.setQuery(e.target.value)}
            value={query}
            InputProps={{
              sx: { bgcolor: 'background.paper' },
              startAdornment: (
                <InputAdornment position={'start'}>
                  <Search color={'primary'} />
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
      </Grid2>
      <PageContent
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          position: 'relative',
        }}
      >
        {isError ? <ExpandedBox>{t('query.error')}</ExpandedBox> : null}
        <AdminUsersTable
          users={users ?? []}
          loading={isFetching}
          paginationModel={paginationModel}
          onPaginationModelChange={handlers.handlePageChange}
          paginationMode={'server'}
          rowCount={rowCount}
        />
      </PageContent>
    </PageLayout>
  );
};
